import React from 'react';
import { Grid } from '@mui/material';
import { segmentsApi } from 'components/audience/segmentsApi';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { v4 as uuid } from 'uuid';
import {
    MESSAGE_AUDIENCE_DELETE_ERROR,
    MESSAGE_AUDIENCE_DELETE_SUCCESS,
    MESSAGE_GRANT_PERK_TO_SEGMENT_ACCEPTED,
    MESSAGE_GRANT_PERK_TO_SEGMENT_REJECTED,
    MESSAGE_RETRY_OPERATION_ACCEPTED,
    MESSAGE_RETRY_OPERATION_REJECTED
} from 'config/messages';
import { IPageOptions } from 'lib/api/paging';
import { updateArrayItem } from 'lib/helpers';
import { useInterval } from 'lib/hooks/useInterval';
import logger from 'lib/logger';
import { isDefined } from 'lib/typeguards';
import { useSnackbar } from 'notistack';
import { MuiTableScrollItems } from 'ui/table/MuiTable';
import { SegmentGrantPerkModal, SegmentGrantPerkModalSubmitData } from './modals/SegmentGrantPerkModal';
import { SegmentRetryOperationModal } from './modals/SegmentRetryOperationModal';
import { Audience } from './models/segmentModel';
import { SegmentOperation } from './models/segmentOperationModel';
import { SegmentOperationProgressBar } from './SegmentOperationProgressBar';
import { SegmentRetryOperationButtonClickData } from './SegmentOperationTable';
import { DeleteDialog } from 'ui/dialogs/DeleteDialog';
import { useNavigate } from 'react-router-dom';
import { MARKETING_AUDIENCES } from 'config/routes';
import { SegmentDetailBetaCardComponent } from './SegmentDetailBetaCardComponent';
import { SegmentOperationBetaTableCardComponent } from './SegmentOperationBetaTableCardComponent';
import { SegmentUserBetaTableCardComponent } from './SegmentUserBetaTableCardComponent';
import { SegmentMetricsBetaOverview } from './SegmentMetricsBetaOverview';
import { AudienceMeasureTotalBoxes } from 'components/analytics/audiences/AudienceTotalBoxes';
import { useViewAudienceAnalyticsQueries } from './hooks/useViewAudienceAnalyticsQueries';
import { DateRangePickerValue } from 'ui/MuiDateRangePicker';
import { sub } from 'date-fns';
import { AudienceAnalyticsTipBanner } from 'components/analytics/audiences/AudienceAnalyticsTipBanner';
import { AUDIENCE_MEASURE_CONFIG_VIEW_AUDIENCE } from 'components/analytics/audiences/model';

const pagingOptions: IPageOptions = { limit: 20, sort: '-createdAt' };

interface SegmentViewProps {
    segment: Audience;
}

// potentially make it editable, but not MVP
const DEFAULT_DATE_RANGE: DateRangePickerValue = {
    startDate: sub(new Date(), { days: 1, months: 3 }),
    endDate: sub(new Date(), { days: 1 })
};

export const AudienceView: React.FC<SegmentViewProps> = ({ segment }) => {
    const { enqueueSnackbar } = useSnackbar();
    const segmentId = segment._id;
    const navigate = useNavigate();
    const [polledOperation, setPolledOperation] = React.useState(undefined);
    const [operations, setOperations] = React.useState([]);
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [deleteLoading, setDeleteLoading] = React.useState(false);
    const [nextOperationKey, setOperationNextKey] = React.useState(undefined);
    const [operationsMounted, setOperationsMounted] = React.useState(false);
    const [operationsLoading, setOperationsLoading] = React.useState(true);

    const [grantPerkModalOpen, setGrantPerkModalOpen] = React.useState(false);
    const openGrantPerkModal = React.useCallback(() => setGrantPerkModalOpen(true), []);
    const closeGrantPerkModal = React.useCallback(() => setGrantPerkModalOpen(false), []);

    const [selectedRetryOperation, setSelectedRetryOperation] = React.useState<SegmentOperation>(undefined);
    const [retryOperationModalOpen, setRetryOperationModalOpen] = React.useState(false);
    const openRetryOperationModal = React.useCallback(() => setRetryOperationModalOpen(true), []);
    const closeRetryOperationModal = React.useCallback(() => setRetryOperationModalOpen(false), []);

    const redirectTimeoutRef = React.useRef(null);

    const {
        error: audienceAnalyticsError,
        measureValues,
        datePoints
    } = useViewAudienceAnalyticsQueries(segmentId, DEFAULT_DATE_RANGE);

    React.useEffect(() => {
        async function getOperationToPoll() {
            try {
                const {
                    body: { items }
                } = await segmentsApi.getSegmentOperationsPaged(
                    segmentId,
                    { status: OperationStatus.PENDING },
                    { limit: 1, sort: '-createdAt' }
                );
                setPolledOperation(items[0]);
            } catch (error) {
                logger.error('Expected operations info but got', error);
            }
        }
        getOperationToPoll();
    }, [segmentId]);

    React.useEffect(() => {
        async function getInitialOperations() {
            try {
                const {
                    body: { items, page }
                } = await segmentsApi.getSegmentOperationsPaged(segmentId, {}, pagingOptions);
                setOperations(items);
                setOperationNextKey(page.nextKey);
            } catch (error) {
                logger.error('Expected operations info but got', error);
            }
            setOperationsLoading(false);
            setOperationsMounted(true);
        }
        getInitialOperations();
    }, [segmentId]);

    useInterval(async () => {
        if (!polledOperation) {
            return;
        }
        if (
            polledOperation.status === OperationStatus.ERROR ||
            polledOperation.status === OperationStatus.TIMEOUT
        ) {
            setPolledOperation(undefined);
            enqueueSnackbar(MESSAGE_GRANT_PERK_TO_SEGMENT_REJECTED(polledOperation.message), {});
            return;
        }
        if (polledOperation.status !== OperationStatus.PENDING) {
            const index = operations.findIndex(item => item._id === polledOperation._id);
            setOperations(updateArrayItem(operations, index, polledOperation));
            setPolledOperation(undefined);
            return;
        }
        const { body, ok } = await segmentsApi.getSegmentOperation(segmentId, polledOperation._id);
        if (!ok) {
            setPolledOperation(undefined);
            enqueueSnackbar(MESSAGE_GRANT_PERK_TO_SEGMENT_REJECTED(body.message), {});
            return;
        }
        setPolledOperation(body);
    }, 1500);

    const handleGrantPerkModalSubmit = React.useCallback(
        async ({ segmentId, perkId, points }: SegmentGrantPerkModalSubmitData) => {
            try {
                const { body } = await segmentsApi.grantPerkToSegment(segmentId, perkId, points, uuid());
                enqueueSnackbar(MESSAGE_GRANT_PERK_TO_SEGMENT_ACCEPTED(body.metadata.perkTitle), {
                    variant: 'success'
                });
                setPolledOperation(body);
                setOperations([body, ...operations]);
            } catch (err) {
                enqueueSnackbar(MESSAGE_GRANT_PERK_TO_SEGMENT_REJECTED(err.message), {
                    variant: 'error'
                });
            }
        },
        [operations, enqueueSnackbar]
    );

    const handleRetryOperationButtonClick = React.useCallback(
        (data: SegmentRetryOperationButtonClickData) => {
            setSelectedRetryOperation(data.operation);
            openRetryOperationModal();
        },
        [setSelectedRetryOperation, openRetryOperationModal]
    );

    const handleRetryOperationDialogConfirm = React.useCallback(async () => {
        try {
            const { body } = await segmentsApi.retryOperation(
                selectedRetryOperation.entityId,
                selectedRetryOperation._id,
                uuid()
            );
            enqueueSnackbar(MESSAGE_RETRY_OPERATION_ACCEPTED, {
                variant: 'success'
            });
            setPolledOperation(body);
            setSelectedRetryOperation(undefined);
            const index = operations.findIndex(item => item._id === selectedRetryOperation._id);
            setOperations([
                body,
                ...updateArrayItem(operations, index, { ...selectedRetryOperation, isRetryable: false })
            ]);
            closeRetryOperationModal();
        } catch (err) {
            enqueueSnackbar(MESSAGE_RETRY_OPERATION_REJECTED(err.message), {
                variant: 'error'
            });
        }
    }, [
        closeRetryOperationModal,
        setSelectedRetryOperation,
        operations,
        selectedRetryOperation,
        enqueueSnackbar
    ]);

    const handleOperationTableScroll = React.useCallback(async () => {
        const {
            body: { items, page }
        } = await segmentsApi.getSegmentOperationsPaged(
            segmentId,
            {},
            { startKey: nextOperationKey, ...pagingOptions }
        );
        setOperations([...operations, ...items]);
        setOperationNextKey(page.nextKey);
    }, [operations, nextOperationKey, segmentId]);

    const handelDeleteOpen = React.useCallback(() => {
        setDeleteOpen(true);
    }, []);

    const handleDeleteClose = React.useCallback(() => {
        setDeleteOpen(false);
    }, []);

    const handleDeleteSubmit = React.useCallback(async () => {
        setDeleteLoading(true);
        const result = await segmentsApi.delete(segment?._id, { skipResponseBody: true });
        if (!result.ok) {
            setDeleteLoading(false);
            setDeleteOpen(false);
            enqueueSnackbar(MESSAGE_AUDIENCE_DELETE_ERROR, { variant: 'error' });
        } else {
            setDeleteOpen(false);
            enqueueSnackbar(MESSAGE_AUDIENCE_DELETE_SUCCESS, { variant: 'success' });
            redirectTimeoutRef.current = setTimeout(() => {
                navigate(MARKETING_AUDIENCES, { replace: true });
            }, 5000);
        }
    }, [navigate, segment?._id, enqueueSnackbar]);

    React.useEffect(
        () => () => {
            if (redirectTimeoutRef.current) {
                clearTimeout(redirectTimeoutRef.current);
            }
        },
        []
    );

    const deleteDialogContent = React.useMemo(
        () =>
            // eslint-disable-next-line max-len
            `There are ${segment?.count} users in this ${segment?.title}. If you delete this audience, you will not be able to recover this audience when deleted. To confirm deletion, type 'DELETE' in the text input field.`,
        [segment?.count, segment?.title]
    );

    const operationTableScrollItems: MuiTableScrollItems = {
        isMoreItems: !!nextOperationKey && operationsMounted,
        key: nextOperationKey,
        onScroll: handleOperationTableScroll
    };

    return (
        <React.Fragment>
            <Grid container spacing={3}>
                {isDefined(polledOperation) && (
                    <Grid
                        size={{
                            xs: 12,
                            md: 12
                        }}
                    >
                        <SegmentOperationProgressBar operation={polledOperation} />
                    </Grid>
                )}
                <Grid
                    size={{
                        xs: 12,
                        md: 12
                    }}
                >
                    <SegmentDetailBetaCardComponent
                        segment={segment}
                        onGrantPerkButtonClick={openGrantPerkModal}
                        onDelete={handelDeleteOpen}
                        disabled={deleteLoading}
                    />
                </Grid>
                {!audienceAnalyticsError && (
                    <Grid size={12}>
                        <AudienceMeasureTotalBoxes
                            config={AUDIENCE_MEASURE_CONFIG_VIEW_AUDIENCE}
                            values={measureValues}
                            datePoints={datePoints}
                        />
                    </Grid>
                )}
                {!audienceAnalyticsError && (
                    <Grid size={12}>
                        <AudienceAnalyticsTipBanner audienceId={segmentId} />
                    </Grid>
                )}
                <Grid
                    size={{
                        xs: 12,
                        md: 3
                    }}
                >
                    <SegmentUserBetaTableCardComponent segment={segment} disabled={deleteLoading} />
                </Grid>
                <Grid
                    size={{
                        xs: 12,
                        md: 9
                    }}
                >
                    {segment?.metrics?.length > 0 && <SegmentMetricsBetaOverview metrics={segment.metrics} />}
                    <SegmentOperationBetaTableCardComponent
                        operations={operations}
                        scroll={operationTableScrollItems}
                        isLoading={operationsLoading}
                        onRetryButtonClick={handleRetryOperationButtonClick}
                    />
                </Grid>
            </Grid>
            <SegmentGrantPerkModal
                open={grantPerkModalOpen}
                onSubmit={handleGrantPerkModalSubmit}
                onClose={closeGrantPerkModal}
                segmentId={segmentId}
            />
            <SegmentRetryOperationModal
                operation={selectedRetryOperation}
                open={retryOperationModalOpen}
                onConfirm={handleRetryOperationDialogConfirm}
                onCancel={closeRetryOperationModal}
            />
            <DeleteDialog
                open={deleteOpen}
                onClose={handleDeleteClose}
                onDelete={handleDeleteSubmit}
                title="Delete audienece"
                content={deleteDialogContent}
                protectionWord="DELETE"
                loading={deleteLoading}
            />
        </React.Fragment>
    );
};
